import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
} from '@mui/material';
import { api } from '../../services/api';
import type { PaymentSummary as PaymentSummaryType, PaymentTransaction } from '../../types/payment';
import { formatTimestampInBrisbane } from '../../utils/dateTime';

interface PaymentSummaryProps {
  orderId: number;
  customerEmail?: string; // Optional email for authentication
  onDataLoaded?: (data: PaymentSummaryType) => void;
  voucherOverride?: number; // Live voucher amount from user input (overrides backend value)
  deliveryFeeOverride?: number; // Live delivery fee from user selection (overrides backend value)
  deliveryFeeLoading?: boolean; // Show "Loading…" for delivery fee (e.g. when toggling to delivery)
  balanceOwingOverride?: number; // Live balance owing calculated in parent (overrides local calculation)
}

/**
 * PaymentSummary Component
 * 
 * Displays complete payment information for an order including:
 * - Order totals and balance owing
 * - Payment transaction history
 * - Locking status indicators
 */
const PaymentSummaryComponent: React.FC<PaymentSummaryProps> = ({ 
  orderId, 
  customerEmail, 
  onDataLoaded, 
  voucherOverride,
  deliveryFeeOverride,
  deliveryFeeLoading,
  balanceOwingOverride 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PaymentSummaryType | null>(null);

  useEffect(() => {
    const fetchPaymentSummary = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[PaymentSummary] Fetching payment summary:', {
          orderId,
          customerEmail: customerEmail ? 'provided' : 'missing',
        });

        // Add email as query parameter for authentication if available
        // Backend should handle missing email gracefully (might use session/auth token)
        const url = customerEmail 
          ? `/v1/payment/orders/${orderId}/summary?email=${encodeURIComponent(customerEmail)}`
          : `/v1/payment/orders/${orderId}/summary`;

        console.log('[PaymentSummary] API URL:', url);

        const response = await api.get(url);

        if (response.data.success) {
          console.log('[PaymentSummary] ✅ Success - API Response:', response.data.data);
          console.log('[PaymentSummary] total_paid:', response.data.data.total_paid);
          console.log('[PaymentSummary] transactions count:', response.data.data.transactions?.length);
          
          setSummary(response.data.data);
          if (onDataLoaded) {
            onDataLoaded(response.data.data);
          }
        } else {
          console.error('[PaymentSummary] ❌ API returned success: false');
          setError(response.data.message || 'Failed to load payment summary');
        }
      } catch (err: any) {
        console.error('[PaymentSummary] ❌ Failed to fetch payment summary:', err);
        console.error('[PaymentSummary] Error details:', {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
        });
        
        const errorMessage = err.response?.data?.message || 'Failed to load payment information';
        
        // If it's an authentication error and email is missing, provide specific message
        if (err.response?.status === 401 || err.response?.status === 403) {
          if (!customerEmail) {
            setError('Email address is required to view payment information. Please ensure your email is entered correctly.');
          } else {
            setError(errorMessage);
          }
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
        console.log('[PaymentSummary] Loading complete');
      }
    };

    // Fetch if we have orderId (email is optional but recommended for authentication)
    // Backend should handle authentication via session/token if email is missing
    if (orderId) {
      fetchPaymentSummary();
    } else {
      console.warn('[PaymentSummary] ⚠️ No orderId provided, cannot fetch payment summary');
      setError('Order ID is required to load payment information');
      setLoading(false);
    }
  }, [orderId, customerEmail, onDataLoaded]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={30} />
          <Typography sx={{ ml: 2 }} color="text.secondary">
            Loading payment information...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!summary) {
    return null;
  }

  /**
   * Calculate display values with overrides from parent component
   * Allows real-time updates as user changes voucher, delivery method, etc.
   * 
   * Priority: Override values (live user input) > Backend values (cached)
   * Round to 2 decimals to avoid floating-point (e.g. "Payment Required" with $0.00 when fully paid).
   */
  const round2 = (n: number) => Math.round(Number(n) * 100) / 100;
  const actualVoucherAmount = round2(voucherOverride !== undefined ? voucherOverride : Number(summary.voucher_amount ?? 0));
  const actualDeliveryFee = round2(deliveryFeeOverride !== undefined ? deliveryFeeOverride : Number(summary.delivery_fee ?? 0));

  // Complete order total includes items + delivery fee
  const orderTotal = round2(Number(summary.total_amount ?? 0) + actualDeliveryFee);

  // Payable amount after voucher discount
  const actualPayableAmount = Math.max(0, round2(orderTotal - actualVoucherAmount));

  // Balance owing: Use override if provided, otherwise calculate locally; round to 2 decimals
  const actualBalanceOwing = round2(
    balanceOwingOverride !== undefined
      ? balanceOwingOverride
      : Math.max(0, actualPayableAmount - Number(summary.total_paid ?? 0))
  );

  // Treat as "zero balance" when within 1 cent (avoids "Payment Required" with $0.00 due to float)
  const isZeroBalance = actualBalanceOwing < 0.01;

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'fully_paid':
        return 'success';
      case 'partially_paid':
        return 'warning';
      case 'overpaid':
        return 'error';
      case 'pending':
      default:
        return 'default';
    }
  };

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case 'fully_paid':
        return 'Fully Paid';
      case 'partially_paid':
        return 'Partially Paid';
      case 'overpaid':
        return 'Overpaid';
      case 'pending':
        return 'Payment Pending';
      default:
        return status;
    }
  };

  // Single source of truth: derive display status from balance so chip and "Payment Complete" stay in sync
  // Use isZeroBalance (tolerance 0.01) to avoid float showing "Payment Required" with $0.00 when fully paid
  const displayPaymentStatus = actualBalanceOwing < -0.01
    ? 'overpaid'
    : isZeroBalance && Number(summary.total_paid ?? 0) > 0
      ? 'fully_paid'
      : summary.payment_status;

  const formatTransactionStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (isoTimestamp: string) => formatTimestampInBrisbane(isoTimestamp) || isoTimestamp;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          Payment Summary
        </Typography>
        <Chip
          label={getPaymentStatusLabel(displayPaymentStatus)}
          color={getPaymentStatusColor(displayPaymentStatus)}
          size="small"
        />
      </Box>

      <List disablePadding>
        {/* Order Total */}
        <ListItem sx={{ px: 0 }}>
          <ListItemText 
            primary="Order Total"
            secondary={summary.items_locked ? "🔒 Items locked" : undefined}
          />
          <Typography variant="body1">
            ${Number(summary.total_amount).toFixed(2)}
          </Typography>
        </ListItem>

        {/* Delivery Fee - show when fee > 0 or when loading (so UI doesn't jump when toggling delivery) */}
        {(actualDeliveryFee > 0 || deliveryFeeLoading) && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText 
              primary="Delivery Fee" 
              secondary={summary.delivery_locked ? "🔒 Delivery locked" : undefined}
            />
            <Typography variant="body1">
              {deliveryFeeLoading ? 'Loading…' : `$${Number(actualDeliveryFee).toFixed(2)}`}
            </Typography>
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Order Total (Items + Delivery) */}
        <ListItem sx={{ px: 0 }}>
          <ListItemText 
            primary="Order Total"
            primaryTypographyProps={{ fontWeight: 'bold' }}
          />
          <Typography variant="body1" fontWeight="bold">
            ${Number(orderTotal).toFixed(2)}
          </Typography>
        </ListItem>

        {/* Voucher */}
        {actualVoucherAmount > 0 && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="Voucher Applied" />
            <Typography variant="body1" color="success.main">
              -${Number(actualVoucherAmount).toFixed(2)}
            </Typography>
          </ListItem>
        )}

        {/* Total Payments Made */}
        {summary.total_paid > 0 && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="Total Payments" />
            <Typography variant="body1" color="success.main">
              -${Number(summary.total_paid).toFixed(2)}
            </Typography>
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Amount Due (Final Balance) */}
        <ListItem sx={{ px: 0 }}>
          <ListItemText
            primary="Amount Due"
            secondary={
              actualVoucherAmount > 0 && summary.total_paid > 0 
                ? "After voucher discount and payments"
                : actualVoucherAmount > 0 
                  ? "After voucher discount"
                  : summary.total_paid > 0
                    ? "After payments"
                    : undefined
            }
            primaryTypographyProps={{ 
              variant: 'h6',
              fontWeight: 'bold',
              color: actualBalanceOwing > 0 ? 'error.main' : 'success.main'
            }}
          />
          <Typography 
            variant="h6"
            fontWeight="bold"
            color={actualBalanceOwing > 0 ? 'error.main' : 'success.main'}
          >
            ${Number(actualBalanceOwing).toFixed(2)}
          </Typography>
        </ListItem>
      </List>

      {/* Payment History */}
      {summary.transactions && summary.transactions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Payment History
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {summary.transactions.map((transaction: PaymentTransaction) => (
              <Box
                key={transaction.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Payment #{transaction.sequence_number}
                    {transaction.payment_intent && (
                      <Chip 
                        label={transaction.payment_intent.replace('_', ' ')}
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatDate(transaction.created_at)}
                  </Typography>
                  {transaction.card_type && transaction.card_last_four && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {transaction.card_type} ****{transaction.card_last_four}
                    </Typography>
                  )}
                  {transaction.error_message && transaction.status !== 'approved' && (
                    <Typography variant="caption" color="error.main" display="block">
                      {transaction.error_message}
                    </Typography>
                  )}
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" fontWeight="bold">
                    ${Number(transaction.amount).toFixed(2)}
                  </Typography>
                  <Chip
                    label={formatTransactionStatus(transaction.status)}
                    size="small"
                    color={
                      transaction.status === 'approved'
                        ? 'success'
                        : ['declined', 'failed', 'cancelled'].includes(transaction.status)
                          ? 'error'
                          : 'default'
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Outstanding Balance Warning - only when balance is meaningfully > 0 (tolerance 0.01) */}
      {!isZeroBalance && actualBalanceOwing >= 0.01 && Number(summary.total_paid ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Payment Required</AlertTitle>
          You have an outstanding balance of ${Number(actualBalanceOwing).toFixed(2)}.
          Please complete payment to finalize your order.
        </Alert>
      )}

      {/* Items Locked Notice */}
      {summary.items_locked && (
        <Alert severity="info" sx={{ mt: 2 }}>
          🔒 <strong>Items Locked:</strong> Your order items cannot be modified after payment has been received.
          Please contact support if you need to make changes.
        </Alert>
      )}

      {/* Delivery Locked Notice */}
      {summary.delivery_locked && (
        <Alert severity="info" sx={{ mt: 2 }}>
          🔒 <strong>Delivery Locked:</strong> You cannot change from delivery to pickup after delivery payment.
          You can still change from pickup to delivery (additional fee will apply).
        </Alert>
      )}

      {/* Overpayment Notice - only when meaningfully overpaid (tolerance 0.01) */}
      {actualBalanceOwing < -0.01 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Overpayment Detected</AlertTitle>
          This order has been overpaid by ${Math.abs(Number(actualBalanceOwing)).toFixed(2)}.
          Please contact our support team for a refund.
        </Alert>
      )}

      {/* Fully Paid Success - use isZeroBalance so float 0.0000001 doesn't hide this */}
      {isZeroBalance && Number(summary.total_paid ?? 0) > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Payment Complete</AlertTitle>
          Your order is fully paid. Thank you!
        </Alert>
      )}
    </Paper>
  );
};

export default PaymentSummaryComponent;
